import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { RevealHeadline } from "../ui/ScrollReveal";
import { IMAGES, JOIN_URL } from "../../data/content";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.95]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100vh] flex flex-col justify-center overflow-hidden bg-[#020617]"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0 z-0" style={{ y: imgY }}>
        <img
          src={IMAGES.heroAthlete}
          alt="Athlete mid-motion representing the MindBodyLife.pro transformation journey"
          className="w-full h-[125%] object-cover object-center opacity-70"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0 z-10 bg-gradient-to-b from-[#020617]/40 via-[#020617]/80 to-[#020617]"
        style={{ opacity: overlayOpacity }}
      />

      <div className="relative z-20 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 px-4 py-1.5 mb-8 shadow-[0_0_15px_rgba(0,240,255,0.15)]"
          data-testid="hero-eyebrow-badge"
        >
          <Sparkles size={13} className="text-cyan-400" />
          <span className="font-mono-brand text-[11px] uppercase tracking-[0.25em] text-cyan-300">
            The Future of Holistic Health Is Here
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
          <RevealHeadline
            testId="hero-headline"
            lines={["Your Complete Health.", "One Powerful Platform."]}
            className="text-white block"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subtext"
        >
          MindBodyLife.pro is a complete Holistic Health Transformation Ecosystem — built to transform your{" "}
          <span className="text-cyan-400 font-semibold">Body, Mind, Gut &amp; Lifestyle</span>, all in one place.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={JOIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-[#020617] shadow-[0_0_30px_rgba(0,240,255,0.4)] hover:shadow-[0_0_45px_rgba(0,240,255,0.6)] hover:scale-[1.03] transition-all duration-300"
            data-testid="hero-cta-primary"
          >
            Start Your 365-Day Journey
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
          </a>
          <a
            href="#active360"
            className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-slate-200 hover:border-cyan-400 hover:text-cyan-300 transition-colors duration-300"
            data-testid="hero-cta-secondary"
          >
            Explore Active360™
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-16 flex flex-wrap gap-6 sm:gap-10"
          data-testid="hero-stats-row"
        >
          {[
            ["₹1", "Per Day"],
            ["365", "Days of Guidance"],
            ["4,000+", "Transformations"],
          ].map(([big, small]) => (
            <div key={small} className="border-l border-cyan-500/30 pl-4">
              <div className="font-display font-extrabold text-2xl sm:text-3xl text-white">{big}</div>
              <div className="font-mono-brand text-[11px] uppercase tracking-widest text-slate-400 mt-1">{small}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
